

import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function ClosedAccountsSection({ caseId, sofaResponse }) {
  const [accounts, setAccounts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({
    institution_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    account_number: '',
    account_type: 'checking',
    date_closed: '',
    last_balance: ''
  });
  const [editAccountData, setEditAccountData] = useState(null);

  useEffect(() => {
    if (!sofaResponse) return;
    async function fetchAccounts() {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-closed-accounts/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch closed accounts');
        setAccounts(await res.json());
      } catch (err) {
        console.error(err);
      }
    }
    fetchAccounts();
  }, [caseId, sofaResponse]);

  function openAddModal() {
    setNewAccount({
      institution_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      account_number: '',
      account_type: 'checking',
      date_closed: '',
      last_balance: ''
    });
    setModalOpen(true);
  }

  function openEditModal(item) {
    setEditAccountData({ ...item });
    setEditModalOpen(true);
  }

  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-closed-accounts/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newAccount)
        }
      );
      if (!res.ok) throw new Error('Failed to add account');
      const created = await res.json();
      setAccounts(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-closed-accounts/${editAccountData.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editAccountData)
        }
      );
      if (!res.ok) throw new Error('Failed to update account');
      const updated = await res.json();
      setAccounts(prev =>
        prev.map(a => (a.id === updated.id ? updated : a))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-closed-accounts/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete account');
      setAccounts(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 20: Closed Accounts
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List financial accounts held in your name or your benefit that have been closed, sole, moved or transfered in the last year. Include checking, savings, and other brokerage accounts.
        </p>
      {accounts.length === 0 ? (
        <p>No closed accounts recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {accounts.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                {/* Trash Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.institution_name}</h3>
              <p className="mt-1 text-sm">
                {item.street1}{item.street2 ? `, ${item.street2}` : ''}, {item.city}, {item.state} {item.zip_code}
              </p>
              {item.account_number && (
                <p className="mt-2 text-sm"><strong>Acct #:</strong> {item.account_number}</p>
              )}
              <p className="mt-1 text-sm"><strong>Type:</strong> {item.account_type}</p>
              <p className="mt-1 text-sm"><strong>Closed:</strong> {item.date_closed || '—'}</p>
              <p className="mt-1 text-sm"><strong>Last Bal:</strong> ${item.last_balance || '0.00'}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Closed Account
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Closed Account</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Institution Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newAccount.institution_name}
                onChange={e => setNewAccount(prev => ({ ...prev, institution_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.street1}
                  onChange={e => setNewAccount(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.street2}
                  onChange={e => setNewAccount(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.city}
                  onChange={e => setNewAccount(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.state}
                  onChange={e => setNewAccount(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.zip_code}
                  onChange={e => setNewAccount(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Account Number</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.account_number}
                  onChange={e => setNewAccount(prev => ({ ...prev, account_number: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Type</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.account_type}
                  onChange={e => setNewAccount(prev => ({ ...prev, account_type: e.target.value }))}
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                  <option value="money_market">Money Market</option>
                  <option value="brokerage">Brokerage</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Date Closed</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.date_closed}
                  onChange={e => setNewAccount(prev => ({ ...prev, date_closed: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Balance</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={newAccount.last_balance}
                  onChange={e => setNewAccount(prev => ({ ...prev, last_balance: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editAccountData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Closed Account</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Institution Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editAccountData.institution_name || ''}
                onChange={e => setEditAccountData(prev => ({ ...prev, institution_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.street1 || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.street2 || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.city || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.state || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.zip_code || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Account Number</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.account_number || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, account_number: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Type</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.account_type || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, account_type: e.target.value }))}
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                  <option value="money_market">Money Market</option>
                  <option value="brokerage">Brokerage</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Date Closed</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.date_closed || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, date_closed: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Balance</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={editAccountData.last_balance || ''}
                  onChange={e => setEditAccountData(prev => ({ ...prev, last_balance: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}